import type { ReactNode } from "react";
import { SeloFaixa, SeloTexto } from "@/modules/visitantes/components/Selos";
import type { Indicacao } from "@/modules/visitantes/services/visitantesService";
import { rotuloSexo } from "@/shared/lib/sexo";
import {
  calcularIdade,
  formatarData,
  formatarTelefone,
  rotuloEstadoCivil,
} from "@/modules/visitantes/utils/regras";

interface Props {
  informado: Indicacao;
  /** "ministerio": quem informou aparece; "autor": o status da avaliação aparece. */
  visao: "ministerio" | "autor";
  children?: ReactNode;
}

const ROTULO_SITUACAO = {
  pendente: { texto: "Aguardando avaliação", tom: "aviso" as const },
  adicionada: { texto: "Adicionado como visitante", tom: "ok" as const },
  descartada: { texto: "Não adicionado", tom: "neutro" as const },
};

/** Cartão de um Visitante pendente (cadastrado por líder) — usado na avaliação e em "Meus pendentes". */
export function CardInformado({ informado: i, visao, children }: Props) {
  const situacao = ROTULO_SITUACAO[i.situacao];
  const avisoNovo = visao === "autor" && i.situacao !== "pendente" && i.aviso_lido === false;
  return (
    <li className="rounded-xl border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-base font-bold text-text">{i.nome}</p>
          <p className="mt-0.5 text-xs text-muted">
            Visitou em {formatarData(i.data_visita)}
            {i.telefone ? ` · ${formatarTelefone(i.telefone)}` : ""}
            {rotuloSexo(i.sexo) ? ` · ${rotuloSexo(i.sexo)}` : ""}
            {rotuloEstadoCivil(i.estado_civil) ? ` · ${rotuloEstadoCivil(i.estado_civil)}` : ""}
            {i.cidade ? ` · ${i.cidade}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {avisoNovo && <SeloTexto tom="aviso">Avaliado — novo aviso</SeloTexto>}
          <SeloTexto tom={situacao.tom}>{situacao.texto}</SeloTexto>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <SeloFaixa idade={calcularIdade(i.nascimento)} />
        {i.editada && i.situacao === "pendente" && (
          <SeloTexto tom="aviso">Editado pelo líder</SeloTexto>
        )}
        {i.familiares && i.familiares.length > 0 && (
          <SeloTexto tom="neutro">
            {`Com ${i.familiares.length} ${i.familiares.length === 1 ? "familiar" : "familiares"}`}
          </SeloTexto>
        )}
      </div>

      {visao === "ministerio" && (
        <p className="mt-2 text-xs text-muted">
          Cadastrado por {i.de ?? "—"}
          {i.ministerio ? ` (${i.ministerio})` : ""} em {formatarData(i.created_at)}
        </p>
      )}
      {visao === "autor" && i.ministerio && (
        <p className="mt-2 text-xs text-muted">Ministério: {i.ministerio}</p>
      )}

      {i.nota && (
        <p className="mt-2 whitespace-pre-line rounded-md bg-surface-2 px-3 py-2 text-sm text-text">
          {i.nota}
        </p>
      )}
      {i.situacao === "descartada" && i.motivo && (
        <p className="mt-2 text-sm text-muted">
          <span className="font-semibold">Motivo:</span> {i.motivo}
        </p>
      )}

      {children && <div className="mt-3 flex flex-wrap gap-2">{children}</div>}
    </li>
  );
}
